import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePrivy } from '@privy-io/react-auth';
import { createTelegramLinkCode, getTelegramBotUsername } from '@/lib/api';

// There's no webhook — the bot confirms the link server-side over its own
// long-polling connection, so the frontend has to poll for the result too.
const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 60_000;

/**
 * Opens the bot deep link in a new tab, falling back to navigating this tab.
 * The link code is fetched first, so by the time we get here the tap's user
 * activation may be gone — in-app browsers / webviews (and strict popup
 * blockers) then refuse the new tab. A same-tab navigation needs no
 * activation. `noopener` is left out of the features string because it makes
 * `window.open` return null even on success, which would hide the failure;
 * the opener is severed by hand instead.
 */
function openDeepLink(url: string) {
  const win = window.open(url, '_blank');
  if (win) {
    win.opener = null;
    return;
  }
  window.location.href = url;
}

/**
 * Drives the "Connect Telegram Bot" flow: fetches a one-time link code,
 * opens the `t.me/<bot>?start=<code>` deep link, then polls
 * `['participantProfile']` for up to a minute waiting for `telegramId` to
 * show up. Also exposes a manual `refreshStatus` for the "Refresh status"
 * button, `stopPolling` so the caller can cut the interval short once the
 * quest status flips to completed, and the `deepLink` itself so the caller
 * can render a tappable fallback if the automatic open was swallowed.
 */
export function useConnectTelegramBot() {
  const { getAccessToken } = usePrivy();
  const qc = useQueryClient();
  const [polling, setPolling] = useState(false);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const pollTimerRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const refreshStatus = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['participantProfile'] });
  }, [qc]);

  // Coming back from Telegram: the tab was backgrounded (timers throttled) or,
  // after the same-tab fallback, restored from bfcache — either way the poll
  // window may have lapsed, so re-check once on return.
  useEffect(() => {
    if (!deepLink) return;
    const onReturn = () => {
      if (document.visibilityState === 'visible') refreshStatus();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('pageshow', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('pageshow', onReturn);
    };
  }, [deepLink, refreshStatus]);

  const startPolling = useCallback(() => {
    stopPolling();
    setPolling(true);
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    pollTimerRef.current = window.setInterval(() => {
      if (Date.now() > deadline) {
        stopPolling();
        return;
      }
      refreshStatus();
    }, POLL_INTERVAL_MS);
  }, [refreshStatus, stopPolling]);

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await getAccessToken();
      if (!token) throw new Error('No Privy session');
      return createTelegramLinkCode(token);
    },
    onSuccess: ({ code }) => {
      const botUsername = getTelegramBotUsername();
      if (botUsername) {
        const url = `https://t.me/${botUsername}?start=${code}`;
        setDeepLink(url);
        openDeepLink(url);
      }
      startPolling();
    },
  });

  return {
    connect: mutation.mutate,
    connecting: mutation.isPending,
    error: mutation.error,
    polling,
    deepLink,
    refreshStatus,
    stopPolling,
  };
}
